<!-- PageErrorLog.vue -->
<script setup lang="ts">
    import { reactive } from 'vue';
    import { UserPageError, UserPageErrorDetails } from '../../../../lib/Api/CustomerSupport';
    import { ICol, RenderModeType } from '../../../../lib/ChoiceTableHelpers';
    import { ChoiceApi } from '../../../../LibMain';
    import { copyToClipboard } from '../../../../lib/Utils/Clipboard';
    import { popAlert } from '../../../../lib/Notification';
    import { DisplayStyle, ButtonDisplaySize, ModalSize } from '../../../../Enums/UiEnums'
    import { ActionButtonStyle } from '../../../../lib/Types/Types';
    import MessageBadge from '../../../../components/Messages/MessageBadge.vue';
    import ChoiceTable from '../../../../components/ChoiceTable.vue';
    import ChoiceTableCell from '../../../../components/Table/ChoiceTableCell.vue';
    import ActionButtonV2 from '../../../../components/ActionButtonV2.vue';
    import ModalDialog from '../../../../components/ModalDialog.vue';
    import ChoiceButton from '../../../../components/Buttons/ChoiceButton.vue';

    const props = defineProps<{
        numberOfDaysOfResults: number,
        userId?: string
    }>();

    const compData = reactive<{
        pageErrorLog?: (UserPageError & PageErrorUiData)[]
        showPageErrorDetails?: boolean,
        pageErrorDetails?: UserPageErrorDetails
    }>({});

    interface PageErrorUiData
    {
        sourcePage?: string,
        sourceQueryString?: string,

    }

    const pageErrorLogColumns: ICol[] = [
        { Name: "Message", DataField: "message", ExtraData: "Message" },
        { Name: "Source", DataField: "source", ExtraData: "Source" },
        { Name: "Page Location", DataField: "location" },
        { Name: "Line Number", DataField: "line" },
        { Name: "Column Number", DataField: "column" },
        { Name: "Timestamp", DataField: "timestamp", RenderMode: RenderModeType.DateTime },
    ];    

    async function getPageErrorDetails(id: string)
    {
        compData.pageErrorDetails = undefined;
        compData.pageErrorDetails = await ChoiceApi.customerSupport.getPageErrorDetails(id);
        compData.showPageErrorDetails = true;
    }

    async function showFullSourceUrl(sourceUrl: string)
    {
        await popAlert(sourceUrl, "Full Source Url");
    }

    function copyDetailsToClipboard(text?: string)
    {
        copyToClipboard(text); 
        popAlert("Copied details to clipboard");     
    }

    function prettyHtml(details?: UserPageErrorDetails)
    {
        if (!details)
            return;

        if ((details.contentType?.indexOf("json") ?? -1) >= 0)
        {
            return prettyJsonHtml(details.details);
        }
        return details.details?.replaceAll("\n", "<br/>")?.replaceAll("\t", "&nbsp;&nbsp;&nbsp;");
    }

    // Convert text to formatted HTML
    function prettyJsonHtml(text?: string)
    {
        if (!text)
            return;
        return JSON.stringify(JSON.parse(text), null, 3).replaceAll('\n', '<br/>').replaceAll('   ', '&nbsp;&nbsp;&nbsp;');
    }

    async function getPageErrorLogData()
    {
        compData.pageErrorLog = undefined;
        compData.pageErrorLog = await ChoiceApi.customerSupport.getLoggedPageErrors(props.numberOfDaysOfResults, props.userId);

        for(const logEntry of (compData.pageErrorLog ?? []))
        {
            if (logEntry.source && logEntry.source.indexOf("?") > 0)
            {
                const pageAndQueryString = logEntry.source.split("?");

                logEntry.sourcePage = pageAndQueryString[0];
                logEntry.sourceQueryString = pageAndQueryString[1];
            }
        }
    }

</script>

<template>
    <div class="mb-1">
        <ActionButtonV2 
            :action="ActionButtonStyle.Binoculars"
            :size="ButtonDisplaySize.Small"
            text="Get Error Data"
            tooltip="Get Page Errors"
            :performAction="async () => await getPageErrorLogData()"   />
        <MessageBadge v-if="props.userId" :displayStyle="DisplayStyle.Warning" class="ms-1">
            Selected UserId: {{ props.userId }}
        </MessageBadge>
    </div>

    <ChoiceTable
        v-if="compData.pageErrorLog"
        :columns="pageErrorLogColumns" 
        :data="compData.pageErrorLog"
        :allowSorting="true"
        :showFilters="true"
        keyValueDataMember="timestamp">
        <template #cell="{value, col, row}">
            <template v-if="'Message' === col.ExtraData">
                <ChoiceTableCell :col="col" :row="row" />
                <span v-if="row.hasDetails" class="ms-1">
                    <ActionButtonV2
                        :action="ActionButtonStyle.Binoculars" 
                        :size="ButtonDisplaySize.Small"
                        text=""
                        tooltip="Show Details" 
                        :performAction="async () => await getPageErrorDetails(row.docId)" />
                </span>
            </template>
            <template v-else-if="'Source' === col.ExtraData">
                {{ row.sourcePage }}
                <span v-if="row.sourceQueryString" class="ms-1">
                    <ActionButtonV2
                        :action="ActionButtonStyle.Binoculars" 
                        :size="ButtonDisplaySize.Small"
                        text=""
                        tooltip="Show Full Url" 
                        :performAction="async () => await showFullSourceUrl(row.source)" />
                </span>
            </template>
            <template v-else>
                <ChoiceTableCell :col="col" :row="row" />
            </template>
        </template>
    </ChoiceTable>

    <ModalDialog v-model:show="compData.showPageErrorDetails" :size="ModalSize.XL">
        <template #title>
            Page Error Details 
        </template>
        <div class="row">
            <div class="col-sm-6">
                <MessageBadge v-if="compData.pageErrorDetails?.contentType">
                    {{ compData.pageErrorDetails?.contentType }}
                </MessageBadge>        
            </div>
            <div class="text-end col-sm-6">
                <ChoiceButton
                :size="ButtonDisplaySize.Small"
                :action="ActionButtonStyle.Clipboard" 
                text=""
                @click="() => copyDetailsToClipboard(compData.pageErrorDetails?.details)" />
            </div>
        </div>
        
        <div style="font-size: small">
            <span v-html="prettyHtml(compData.pageErrorDetails)" />            
        </div>

    </ModalDialog>    

</template>